<!-- Copyright 2025, Common Good Learning Tools LLC -->
<template>
	<div class="k-asset-manager k-main-collection">
		<h1 class="k-page-title d-flex">
			<k-icon color="primary" class="mr-3" large @click="$emit('return')">fas fa-file-certificate</k-icon>
			{{site_config.asset_manager.text.name}}
			<k-spacer/>

			<k-menu v-if="user_is_admin && !basic_view" transition="scale-transition" :close-on-content-click="false" offset-y>
				<template v-slot:activator="{ on: menu, attrs }">
					<span>
						<k-tooltip bottom>
							<template v-slot:activator="{ on: tooltip }">
								<k-btn v-bind="attrs" v-on="{ ...tooltip, ...menu }" aria-label="Column Options" class="mr-2" style="margin-top: -6px;" icon x-small>
									<k-icon>fas fa-bars</k-icon>
								</k-btn>
							</template>
							Column Options
						</k-tooltip>
					</span>
				</template>

				<k-list dense>
					<k-list-item
						v-for="header in headers.filter(h => h.value != 'button' && h.value != 'actions')"
						:key="header.value"
						class="k-header-option"
						@click="toggle_column(header)"
					>
						<k-list-item-title style="cursor: pointer;">
							<k-icon class="mr-2" :color="header.show ? 'green' : ''" small>{{ header.show ? 'fas fa-check-square' : 'fas fa-circle-xmark' }}</k-icon>
							{{ header.text }}
						</k-list-item-title>
					</k-list-item>
				</k-list>
			</k-menu>

			<k-tooltip v-if="payments_enabled && user_is_admin && !basic_view && !payment_data" bottom>
				<template v-slot:activator="{ on }">
					<k-btn v-on="on" aria-label="Load Payment Data" class="mt-1" icon x-small @click="load_payment_data">
						<k-icon>fas fa-dollar-sign</k-icon>
					</k-btn>
				</template>
				Load Payment Data
			</k-tooltip>

			<k-btn v-if="user_is_admin" color="tertiary" depressed small @click="basic_view = !basic_view">{{ basic_view ? 'Detail View' : 'Basic View'}}</k-btn>
		</h1>

		<k-tabs v-model="tab" bg-color="primary" style="border-radius: 6px 6px 0 0;">
			<k-tab
				v-for="(item, index) in tabs"
				:key="item.key"
				class="k-tight-btn k-nocaps-btn"
				:class="{'k-admin-tabs-start': item.admin && !tabs[index - 1].admin}"
				style="font-size:16px"
			><b>{{ item.text }}</b></k-tab>

			<k-spacer/>

			<k-tooltip bottom>
				<template v-slot:activator="{on}">
					<k-btn v-on="on" class="ma-2" icon aria-label="Refresh" @click="refresh">
						<k-icon>fas fa-refresh</k-icon>
					</k-btn>
				</template>
				Refresh
			</k-tooltip>
		</k-tabs>

		<div v-if="tab < 3" class="d-flex justify-content-center flex-fill pa-3" style="background-color: #fff;">
			<k-text-field
				v-model="filter.term"
				prepend-inner-icon="fa fa-filter"
				clear-icon="fa fa-times-circle"
				label="Search"
				background-color="#fff"
				class="pr-3"
				clearable
				single-line
				hide-details
				outlined
				dense
			/>
			
			<k-select
				v-model="filter.status"
				:items="filter_statuses"
				placeholder="All Statuses"
				class="pr-3"
				solo outlined dense flat hide-details
			/>
			
			<k-select
				v-model="filter.subject"
				:items="filter_subjects"
				placeholder="All Subjects"
				class="pr-3"
				solo outlined dense flat hide-details
			/>
			
			<k-select
				v-model="filter.grade"
				:items="filter_grades"
				placeholder="All Grades"
				solo outlined dense flat hide-details
			/>
		</div>

		<k-tabs-items v-model="tab" style="border-radius: 0 0 6px 6px;">
			<k-tab-item v-for="(tab) in tabs" :key="tab.key" class="table-wrapper">
				<AssetLookupTable
					v-if="tab.key === 'published' || tab.key === 'unsubmitted'"
					:publish_status="tab.key"
					:subjects="filter_subjects"
					:grades="filter_grades"
					:ref="'table_' + tab.key"
					@show_status="show_status($event)"
				/>

				<AssetPaymentTracking
					v-else-if="tab.key === 'payments'"
					:ref="'table_' + tab.key"
					@show_status="show_status($event)"
				/>

				<AssetPaymentTracking
					v-else-if="tab.key === 'completed_payments'"
					:ref="'table_' + tab.key"
					completed
					@show_status="show_status($event)"
				/>

				<AssetManagerTable
					v-else
					:tab="tab"
					:headers="headers"
					:filter="filter"
					:basic_view="basic_view"
					:payment_data="payment_data"
					:load_payment_data="load_payment_data"
					:update_payment_data="update_payment_data"
					:ref="'table_' + tab.key"
					@show_status="show_status($event)"
				/>
			</k-tab-item>
		</k-tabs-items>

		<ResourceCollectionItem
			v-if="item_to_show"
			:quick_look_only="true"
			:open_to_publish_status="true"
			:item="item_to_show.asset"
			@quick_look_closed="item_to_show = undefined"
			@edit_item_saved="asset_saved"
		/>
	</div>
</template>

<script>
import { mapState } from 'vuex'
import AssetManagerTable from './AssetManagerTable.vue'
import AssetLookupTable from './AssetLookupTable.vue'
import AssetPaymentTracking from './AssetPaymentTracking.vue'
import ResourceCollectionItem from '../resources/ResourceCollectionItem.vue'

export default {
	name: 'AssetManager',

	components: {AssetManagerTable, AssetLookupTable, AssetPaymentTracking, ResourceCollectionItem},

	data() {
		return {
			tab: 0,
			options: {
				groupBy: [],
				groupDesc: [],
				itemsPerPage: 100,
				multiSort: false,
				mustSort: false,
				page: 1,
				sortBy: [],
				sortDesc: [false]
			},
			footerProps: {
				'items-per-page-options': [100, 50, 25, 10]
			},
			status: {
				show: false,
				item: undefined,
				type: undefined,
				roles: undefined
			},
			selected: [],
			resource_showing: undefined,
			resource_being_edited: undefined,
			lesson_being_edited: undefined,
			lesson_showing: undefined,
			item_to_show: null,
			headers: [
				{ text: 'Actions', align: 'center', sortable: false, value: 'actions', basic: false, show: true },
				{ align: 'center', sortable: false, value: 'button', basic: true, show: true },
				{ text: 'Title', align: 'start', sortable: true, width: '200px', value: 'title', basic: true, show: true },
				{ text: 'ID', align: 'start', sortable: true, value: 'id', basic: false, show: true },
				{ text: 'Submitted Date', align: 'center', sortable: true, value: 'submitted_date', basic: true, show: true },
				{ text: 'Status Date', align: 'center', sortable: true, value: 'status_date', basic: true, show: true },
				{ text: 'Status', align: 'center', sortable: true, value: 'publish_status', basic: true, show: true },
				{ text: 'Author', align: 'center', sortable: true, value: 'creator_name', basic: true, show: true },
				{ text: 'Reviewer 1', align: 'center', sortable: true, value: 'reviewer_1', basic: false, show: true },
				{ text: 'Reviewer 1 Approved', align: 'center', sortable: true, value: 'reviewer_1_approved', basic: false, show: true },
				{ text: 'Reviewer 2', align: 'center', sortable: true, value: 'reviewer_2', basic: false, show: true },
				{ text: 'Reviewer 2 Approved', align: 'center', sortable: true, value: 'reviewer_2_approved', basic: false, show: true },
				{ text: 'Other Reviewers', align: 'center', sortable: false, value: 'other_reviewers', basic: false, show: false },
				{ text: 'Subject', align: 'center', sortable: false, value: 'subjects', basic: true, show: true },
				{ text: 'Grade', align: 'center', sortable: false, value: 'grades', basic: true, show: true },
				{ text: 'Alignments', align: 'center', sortable: false, value: 'alignments', basic: true, show: true },
			],
			filter: {
				term: undefined,
				grade: undefined,
				subject: undefined,
				status: undefined,
			},
			basic_view: true,
			payment_data: null,
			loading_payment_data: false,
		}
	},

	computed: {
		...mapState(['user_info', 'site_config']),

		user_is_admin() {
			return this.user_info.system_role == 'admin' || this.user_info.admin_rights.includes('su')
		},

		payments_enabled() {
			return this.site_config.asset_manager.payment_tracking_enabled
		},

		tabs() {
			let tabs = [
				{
					key: 'lesson',
					text: 'Lessons',
				},
				{
					key: 'activity',
					text: `${this.site_config.sparkl_app_name} Activities`,
				},
				{
					key: 'resource',
					text: 'Resources',
				},
			]

			// assets outside the review process; these lists can be huge, so they're loaded on demand with server-side filters
			if (this.user_is_admin) {
				tabs.push({key: 'published', text: 'Published', admin: true})
				// not submitted, unpublished, or rejected
				tabs.push({key: 'unsubmitted', text: 'Not in Review', admin: true})
			}

			if (this.site_config.asset_manager.payment_tracking_enabled && this.user_is_admin) {
				tabs.push({key: 'payments', text: 'Pending Payments', admin: true})
				tabs.push({key: 'completed_payments', text: 'Completed Payments', admin: true})
			}

			return tabs
		},

		filter_grades() {
			let arr = [{value: 'all', text: '--- ALL ---'}]

			this.$store.state.grades.forEach(value => {
				let text = value

				switch(value) {
					case 'PK':
						text = 'Pre-K'
						break
					case 'K':
						text = 'Kindergarten'
						break
					default:
						if (!isNaN(value * 1)) text = 'Grade ' + value
				}

				arr.push({value, text})
			})
			
			return arr
		},

		filter_subjects() {
			let arr = [{value: 'all', text: '--- ALL ---'}]

			Object.keys(this.$store.state.subjects).forEach(value => {
				arr.push({value, text: value})
			})
			
			return arr
		},

		filter_statuses() {
			let arr = [{value: 'all', text: '--- ALL ---'}]
			let statuses = this.$get_publish_statuses()

			Object.values(statuses).forEach(x => {
				if (x.key != 'published' && x.key != 'unsubmitted') {
					arr.push({value: x.key, text: x.text})
				}
			})
			
			return arr
		},

		lesson_editor_title_style() {
			// try to make sure lesson title fits in the space we have alloted; if it doesn't, we get an ugly extra scroll bar
			let s = U.get_max_font_size_for_block_width({size_hi:18, size_lo:12, width:700, val:this.lesson_being_edited.lesson_title})
			return `font-size:${s}px`
		},
	},

	watch: {
		'filter.status'() {
			if (this.filter.status == 'all') {
				this.filter.status = null
			}
		},
		'filter.grade'() {
			if (this.filter.grade == 'all') {
				this.filter.grade = null
			}
		},
		'filter.subject'() {
			if (this.filter.subject == 'all') {
				this.filter.subject = null
			}
		},
	},

	created() {
		if (this.user_is_admin) this.basic_view = false

		let saved_column_visibility = U.local_storage_get('asset_manager_column_visibility', {})
		this.headers.forEach(header => {
			if (header.value in saved_column_visibility) header.show = saved_column_visibility[header.value]
		})
	},

	mounted() {
		vapp.asset_manager_component = this
	},

	methods: {
		show_status(item) {
			this.item_to_show = item
		},

		toggle_column(header) {
			header.show = !header.show

			let saved_column_visibility = U.local_storage_get('asset_manager_column_visibility', {})
			saved_column_visibility[header.value] = header.show
			U.local_storage_set('asset_manager_column_visibility', saved_column_visibility)
		},

		async load_payment_data() {
			// don't re-fetch if we already have the data, or if a fetch is already in progress
			if (this.payment_data || this.loading_payment_data) return

			let asset_ids = []
			this.tabs.filter(tab => ['lesson', 'activity', 'resource'].includes(tab.key)).forEach(tab => {
				let table = this.$refs['table_' + tab.key]?.[0]
				if (table) {
					table.items.forEach(item => {
						asset_ids.push(item.asset_class_type == 'lesson' ? item.asset.lesson_id : item.asset.resource_id)
					})
				}
			})

			if (!asset_ids.length) return

			this.loading_payment_data = true

			U.loading_start()
			let result = await new Promise(resolve => U.ajax('get_asset_review_payment', {user_id: this.user_info.user_id, asset_ids}, resolve))
			U.loading_stop()

			this.loading_payment_data = false

			if (result.status != 'ok') {
				console.error('something went wrong: ', result)
				return
			}

			this.payment_data = result.payment_data ?? []
		},

		update_payment_data(payment_data) {
			if (!this.payment_data) return

			let index = this.payment_data.findIndex(p => p.asset_id == payment_data.asset_id)
			if (index == -1) {
				this.payment_data.push(payment_data)
			} else {
				this.payment_data.splice(index, 1, payment_data)
			}
		},

		asset_saved($event) {
			if ($event.type == 'lesson') {
				// TODO: I think we have to do something to preserve *_showing values, using $event.edited_lesson
				this.item_to_show.asset = new Lesson($event.updated_lesson)
			} else {
				this.item_to_show.asset = new Resource($event.updated_resource)
			}
		},

		lesson_saved(lesson) {
			this.status.item = new Lesson(lesson.updated_lesson)
			if (lesson.flag === 'and_close'){
				this.lesson_being_edited =  undefined
			}
		},

		resource_saved(resource) {
			this.status.item = new Resource(resource)
			this.resource_being_edited = undefined
		},

		status_updated(item) {
			this.status.item = item
		},

		refresh() {
			let tab = this.tabs[this.tab]
			this.$refs['table_' + tab.key][0]
				.load_assets()
				.then(() => {
					this.$inform('Data refreshed')
				})
		},
	}
}
</script>

<style lang="scss" scoped>
.k-asset-manager {
	text-align: left;
	width: 100vw;
	display: flex;
	flex-direction: column;
	overflow: auto;
	background-color: rgba(255, 255, 255, 0.85);
    padding: 15px 80px 40px 80px;
    margin: 0 auto;

	.table-wrapper {
		position: relative;
	}
}

// divider between the standard tabs and the admin-only tabs; uses ::after because vuetify uses a tab's ::before for its hover overlay
.k-admin-tabs-start {
	position: relative;
	margin-left: 16px;

	&::after {
		content: '';
		position: absolute;
		left: -9px;
		top: 25%;
		height: 50%;
		// fixed color (vuetify's inactive tab text color at 50%) so the divider doesn't change when this tab is active
		border-left: 2px solid rgba(0, 0, 0, 0.27);
	}
}

.k-filter {
	&-group {
		width: 100%;
		justify-content: space-between;
		position: relative;
	}

	&-field {
		width: 18% !important;
	}
}

@media (max-width: 1280px) {
    .k-asset-manager {
        padding-left: 40px;
        padding-right: 40px;
    }
}
</style>

<style lang="scss">
.load-btn {
    position: absolute;
    right: 12px;
    top: 12px;
}

.v-application--is-ltr .v-data-table--fixed-header .v-data-footer {
	margin-right: unset !important;
}

.k-header-option:hover {
	background-color: lightgray;
}
</style>
